import * as React from "react";
import { format, parseISO, isValid as isValidDate } from "date-fns";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useNavigation, type CaptionProps, type Matcher } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Parse a Date | ISO / yyyy-MM-dd string | null into a valid Date or undefined.
 *  Guards against unparseable stored values — date-fns format() throws a
 *  RangeError on Invalid Date, which would crash the whole form on render. */
function toValidDate(value?: Date | string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = value instanceof Date ? value : parseISO(value);
  return isValidDate(parsed) ? parsed : undefined;
}

/**
 * Month + year dropdowns so any date is one click away — no repeated prev/next
 * paging to reach a birth year decades back or an expiry years out. Built by a
 * factory bound to a [fromYear, toYear] range so the same caption serves both
 * past-only (DOB) and future (expiry) pickers.
 */
function createDropdownCaption(fromYear: number, toYear: number) {
  const years: number[] = [];
  for (let y = toYear; y >= fromYear; y--) years.push(y);

  return function DropdownCaption({ displayMonth }: CaptionProps) {
    const { goToMonth, previousMonth, nextMonth } = useNavigation();
    return (
      <div className="flex items-center justify-between px-1 pt-1 pb-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => previousMonth && goToMonth(previousMonth)}
          disabled={!previousMonth}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border bg-transparent hover:bg-accent disabled:opacity-30"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1">
          <Select
            value={String(displayMonth.getMonth())}
            onValueChange={(v) =>
              goToMonth(new Date(displayMonth.getFullYear(), parseInt(v, 10), 1))
            }
          >
            <SelectTrigger className="h-8 w-[104px] border-input text-sm shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-56">
              {MONTHS.map((m, i) => (
                <SelectItem key={m} value={String(i)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(displayMonth.getFullYear())}
            onValueChange={(v) =>
              goToMonth(new Date(parseInt(v, 10), displayMonth.getMonth(), 1))
            }
          >
            <SelectTrigger className="h-8 w-[76px] border-input text-sm shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-56">
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          aria-label="Next month"
          onClick={() => nextMonth && goToMonth(nextMonth)}
          disabled={!nextMonth}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border bg-transparent hover:bg-accent disabled:opacity-30"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    );
  };
}

export type DatePickerProps = {
  /** Date, ISO / yyyy-MM-dd string, or null/undefined for empty. */
  value?: Date | string | null;
  /** Receives the picked Date, or null when cleared. */
  onChange: (date: Date | null) => void;
  placeholder?: string;
  /** Earliest year offered in the dropdown. Default: current year − 100. */
  fromYear?: number;
  /** Latest year offered in the dropdown. Default: current year + 10. */
  toYear?: number;
  /** react-day-picker disabled matcher (e.g. block future dates for a DOB). */
  disabled?: Matcher | Matcher[];
  /** Month shown when opened with no value. Default: today. */
  defaultMonth?: Date;
  /** date-fns format for the trigger label. Default: "PPP". */
  labelFormat?: string;
  /** Extra classes for the trigger button. */
  className?: string;
  align?: "start" | "center" | "end";
  /**
   * Rendered inside the popover, below the calendar (e.g. a time picker) —
   * centered, with a top divider. Receives `close` to dismiss the popover
   * (e.g. from a Done button). When provided, picking a day does NOT auto-close
   * the popover, so the extra controls stay reachable.
   */
  footer?: (opts: { close: () => void }) => React.ReactNode;
} & Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  "value" | "onChange" | "disabled"
>;

/**
 * Single-date picker with a month/year dropdown and a fixed-size popover:
 * `fixedWeeks` keeps the grid six rows tall so it never resizes while paging
 * months. forwardRef + prop spread let it drop straight into a shadcn
 * <FormControl> (id / aria-invalid / aria-describedby land on the trigger).
 */
const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder = "Pick a date",
      fromYear,
      toYear,
      disabled,
      defaultMonth,
      labelFormat = "PPP",
      className,
      align = "start",
      footer,
      ...buttonProps
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const selected = toValidDate(value);
    const currentYear = new Date().getFullYear();
    const from = fromYear ?? currentYear - 100;
    const to = toYear ?? currentYear + 10;
    const Caption = React.useMemo(
      () => createDropdownCaption(from, to),
      [from, to],
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            data-vaul-no-drag
            className={cn(
              "w-full justify-start text-left font-normal shadow-none drop-shadow-none",
              !selected && "text-muted-foreground",
              className,
            )}
            {...buttonProps}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            {selected ? format(selected, labelFormat) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-auto p-0">
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(date) => {
                onChange(date ?? null);
                // Keep the popover open when a footer (e.g. a time picker) is
                // present so its controls stay reachable after picking a day.
                if (date && !footer) setOpen(false);
              }}
              defaultMonth={selected ?? defaultMonth}
              disabled={disabled}
              fixedWeeks
              showOutsideDays
              initialFocus
              className="w-[17.5rem] rounded-md bg-white"
              classNames={{
                // The month/year dropdown caption is wider than the default
                // 7×2rem day grid, which left dead space on the right. Fixing
                // the calendar width and spreading the 7 columns across it
                // (justify-between → last column hugs the right edge) keeps the
                // caption, header and weeks all the same width.
                head_row: "flex w-full justify-between",
                row: "flex w-full justify-between mt-2",
              }}
              components={{ Caption }}
            />
          </div>
          {footer && (
            <div className="flex items-center justify-center gap-1.5 border-t p-3">
              {footer({ close: () => setOpen(false) })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);
DatePicker.displayName = "DatePicker";

export default DatePicker;
